import { h } from 'preact'
import { useCallback, useEffect, useState } from 'preact/hooks'
import {
  drive_index_path,
  drive_path,
  files_move_drive_index_path,
  folders_drive_index_path
} from '../../routes'
import { useBootstrapModal, csrfToken } from '../../modules/drive/bootstrap-modal.js'
import { useDriveStore, useDriveI18n } from './drive-context.js'

export function MoveModal({ open, onClose }) {
  const store = useDriveStore()
  const i18n = useDriveI18n()
  const t = (key, opts) => i18n.t(key, opts)

  const [navStack, setNavStack] = useState([])
  const [folders, setFolders] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [highlightId, setHighlightId] = useState(null)
  const [busy, setBusy] = useState(false)

  const handleHidden = useCallback(() => onClose?.(), [onClose])
  const modalRef = useBootstrapModal(open, handleHidden)

  const selectedIds = store.selectedIds.value
  const blocked = new Set(
    selectedIds.filter((k) => k.startsWith('folder-')).map((k) => String(k.replace('folder-', '')))
  )

  const loadFolders = useCallback(async (parentId) => {
    setLoading(true)
    setError(null)
    try {
      const url = parentId
        ? `${folders_drive_index_path({ format: 'json' })}?parent_id=${encodeURIComponent(parentId)}`
        : folders_drive_index_path({ format: 'json' })
      const res = await fetch(url, {
        headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken() }
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      setFolders((data || []).filter((f) => !blocked.has(String(f.id))))
    } catch {
      setError(t('drive.move_folder_load_error'))
      setFolders([])
    } finally {
      setLoading(false)
    }
  }, [blocked, t])

  useEffect(() => {
    if (!open) return
    setNavStack([])
    setHighlightId(null)
    setBusy(false)
    loadFolders(null)
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  function navigateInto(folder) {
    setNavStack((s) => [...s, { id: folder.id, name: folder.name }])
    setHighlightId(null)
    loadFolders(folder.id)
  }

  function navigateToIndex(index) {
    if (index < 0) {
      setNavStack([])
      setHighlightId(null)
      loadFolders(null)
      return
    }
    setNavStack((s) => s.slice(0, index + 1))
    setHighlightId(null)
    const parent = navStack[index]
    loadFolders(parent?.id ?? null)
  }

  async function confirmMove(targetFolderId) {
    const ids = store.selectedIds.value
    if (!ids.length) return
    const fileIds = ids.filter((k) => k.startsWith('file-')).map((k) => k.replace('file-', ''))
    const folderIds = ids.filter((k) => k.startsWith('folder-')).map((k) => k.replace('folder-', ''))
    if (targetFolderId != null && folderIds.map(String).includes(String(targetFolderId))) {
      alert(t('drive.move_invalid_target'))
      return
    }
    setBusy(true)
    try {
      const tasks = []
      if (fileIds.length) {
        tasks.push(fetch(files_move_drive_index_path(), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken()
          },
          body: JSON.stringify({ file_ids: fileIds, target_folder_id: targetFolderId })
        }).then((r) => { if (!r.ok) throw new Error('move files failed') }))
      }
      folderIds.forEach((folderId) => {
        tasks.push(fetch(drive_path(folderId, { format: 'json' }), {
          method: 'PATCH',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken()
          },
          body: JSON.stringify({ drive_folder: { parent_id: targetFolderId } })
        }).then((r) => { if (!r.ok) throw new Error('move folder failed') }))
      })
      await Promise.all(tasks)
      store.clearSelection()
      await store.loadFolder(window.location.pathname + '.json' + window.location.search)
      onClose?.()
    } catch (e) {
      console.error(e)
      alert(t('drive.error'))
    } finally {
      setBusy(false)
    }
  }

  const title = selectedIds.length === 1
    ? t('drive.move_to_folder_title')
    : t('drive.move_title_multiple', { count: selectedIds.length })

  const destId = highlightId != null
    ? highlightId
    : (navStack[navStack.length - 1]?.id ?? null)

  return (
    <div class="modal fade" id="driveMoveFolderModal" tabindex="-1" aria-hidden="true" ref={modalRef}>
      <div class="modal-dialog modal-dialog-scrollable modal-lg">
        <div class="modal-content drive-move-modal">
          <div class="modal-header flex-column align-items-stretch gap-1">
            <div class="d-flex align-items-start justify-content-between gap-2">
              <h5 class="modal-title mb-0"><i class="bi bi-folder-symlink me-2" />{title}</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label={t('drive.close')} />
            </div>
          </div>
          <div class="modal-body p-0">
            <div class="drive-move-modal__nav border-bottom">
              <div class="drive-move-modal__crumbs d-flex align-items-center flex-nowrap px-3 py-2 overflow-auto">
                <button type="button" class="drive-move-modal__crumb btn btn-link btn-sm text-decoration-none px-1" onClick={() => navigateToIndex(-1)}>
                  <i class="bi bi-hdd me-1" />{t('drive.move_folder_root')}
                </button>
                {navStack.map((item, index) => (
                  <span key={item.id} class="d-flex align-items-center">
                    <span class="text-muted px-1">/</span>
                    {index === navStack.length - 1 ? (
                      <span class="drive-move-modal__crumb is-current px-1 fw-medium text-truncate">{item.name}</span>
                    ) : (
                      <button type="button" class="drive-move-modal__crumb btn btn-link btn-sm text-decoration-none px-1 text-truncate" onClick={() => navigateToIndex(index)}>
                        {item.name}
                      </button>
                    )}
                  </span>
                ))}
              </div>
            </div>
            <div class="drive-move-modal__list">
              {loading && (
                <div class="text-center py-4">
                  <div class="spinner-border spinner-border-sm text-secondary" role="status" />
                </div>
              )}
              {error && <div class="alert alert-danger m-3 mb-0"><i class="bi bi-exclamation-triangle me-2" />{error}</div>}
              {!loading && !error && folders.length === 0 && (
                <div class="text-muted text-center py-4 small">{t('drive.move_folder_empty')}</div>
              )}
              {!loading && folders.map((folder) => (
                <div
                  key={folder.id}
                  class={`drive-move-modal__row${highlightId === String(folder.id) ? ' is-selected' : ''}`}
                  role="option"
                  tabindex="0"
                  onClick={() => setHighlightId(String(folder.id))}
                  onDblClick={() => folder.has_children && navigateInto(folder)}
                >
                  <div class="drive-move-modal__row-main text-truncate">
                    <i class="bi bi-folder-fill text-warning me-2" aria-hidden="true" />
                    <span class="fw-medium">{folder.name}</span>
                  </div>
                  <div class="drive-move-modal__row-actions">
                    <button
                      type="button"
                      class="btn btn-sm btn-dark drive-move-modal__move-btn"
                      onClick={(e) => { e.stopPropagation(); confirmMove(folder.id) }}
                    >
                      {t('drive.move')}
                    </button>
                    {folder.has_children ? (
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-secondary drive-move-modal__open-btn"
                        title={t('drive.move_folder_open')}
                        onClick={(e) => { e.stopPropagation(); navigateInto(folder) }}
                      >
                        <i class="bi bi-chevron-right" aria-hidden="true" />
                      </button>
                    ) : (
                      <span class="drive-move-modal__open-spacer" aria-hidden="true" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div class="modal-footer">
            <p class="drive-move-modal__hint small text-muted me-auto mb-0">{t('drive.move_path_hint')}</p>
            <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">{t('drive.cancel')}</button>
            <button type="button" class="btn btn-primary" disabled={busy} onClick={() => confirmMove(destId)}>
              {t('drive.move')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
